export const NUTRIENTS = [
  ["calorias_kcal", "kcal"],
  ["proteina_g", "g proteína"],
  ["carboidrato_g", "g carboidrato"],
  ["gordura_g", "g gordura"],
] as const;

export type NutrientKey = (typeof NUTRIENTS)[number][0];

export const VALUE_ORIGINS = [
  "nao_informado",
  "digitado_nao_validado",
  "gerado_nao_validado",
  "fonte_validada",
] as const;

export type ValueOrigin = (typeof VALUE_ORIGINS)[number];

export interface Ordered {
  ordem?: number | null;
}

export function ordered<T extends Ordered>(rows: readonly T[] | null | undefined): T[] {
  return [...(rows ?? [])].sort((a, b) => Number(a.ordem ?? 0) - Number(b.ordem ?? 0));
}

export interface NutritionReplacement extends Ordered {
  id: string;
  alimento: string;
  quantidade: number;
  unidade: string;
  valores_origem: string;
  valores_fonte: string | null;
  calorias_kcal?: number | null;
  proteina_g?: number | null;
  carboidrato_g?: number | null;
  gordura_g?: number | null;
  observacoes?: string | null;
}

export interface NutritionItem extends NutritionReplacement {
  plano_item_substituicoes?: NutritionReplacement[] | null;
}

export interface NutritionMeal extends Ordered {
  id: string;
  nome: string;
  horario: string | null;
  observacoes?: string | null;
  plano_refeicao_itens?: NutritionItem[] | null;
}

export interface NutritionPlanLike {
  id: string;
  nome: string;
  status: string | null;
  versao: number;
  plano_refeicoes?: NutritionMeal[] | null;
}

export interface NormalizedMeal extends NutritionMeal {
  plano_refeicao_itens: (NutritionItem & { plano_item_substituicoes: NutritionReplacement[] })[];
}

export function meals(plan: NutritionPlanLike | null | undefined): NormalizedMeal[] {
  return ordered(plan?.plano_refeicoes).map((meal) => ({
    ...meal,
    plano_refeicao_itens: ordered(meal.plano_refeicao_itens).map((item) => ({
      ...item,
      plano_item_substituicoes: ordered(item.plano_item_substituicoes),
    })),
  }));
}

export function planCounts(plan: NutritionPlanLike | null | undefined): {
  meals: number;
  items: number;
  substitutions: number;
} {
  const normalized = meals(plan);
  return {
    meals: normalized.length,
    items: normalized.reduce((sum, meal) => sum + meal.plano_refeicao_itens.length, 0),
    substitutions: normalized.reduce(
      (sum, meal) =>
        sum +
        meal.plano_refeicao_itens.reduce(
          (itemSum, item) => itemSum + item.plano_item_substituicoes.length,
          0,
        ),
      0,
    ),
  };
}

export function hasNutritionValues(row: Record<string, unknown> | null | undefined): boolean {
  return NUTRIENTS.some(([key]) => {
    const value = row?.[key];
    return value !== null && value !== undefined && value !== "";
  });
}

/**
 * A proveniência é parte do dado, não enfeite: valor sem base validada precisa
 * dizer que não foi validado.
 */
export function provenanceLabel(origin: string | null | undefined): string {
  return (
    {
      nao_informado: "Sem valores nutricionais informados",
      digitado_nao_validado: "Digitado — não validado por base nutricional",
      gerado_nao_validado: "Gerado — não validado por base nutricional",
      fonte_validada: "Fonte nutricional identificada",
    }[String(origin)] ?? "Origem não identificada"
  );
}

export function nutritionSummary(row: Record<string, unknown> | null | undefined): string {
  return NUTRIENTS.filter(([key]) => {
    const value = row?.[key];
    return value !== null && value !== undefined && value !== "";
  })
    .map(([key, unit]) => `${Number(row?.[key]).toLocaleString("pt-BR")} ${unit}`)
    .join(" · ");
}

export interface PlanPayloadRow {
  alimento: string;
  quantidade: number | string;
  unidade: string;
  calorias_kcal?: number | string | null;
  proteina_g?: number | string | null;
  carboidrato_g?: number | string | null;
  gordura_g?: number | string | null;
  valores_origem: string;
  valores_fonte?: string | null;
  observacoes?: string | null;
}

export interface PlanPayloadItem extends PlanPayloadRow {
  substituicoes?: PlanPayloadRow[];
}

export interface PlanPayloadMeal {
  nome: string;
  horario?: string | null;
  observacoes?: string | null;
  itens: PlanPayloadItem[];
}

export interface NutritionPlanPayload {
  nome: string;
  status: string;
  objetivo?: string | null;
  data_inicio?: string | null;
  data_fim_prevista?: string | null;
  meta_energia_kcal?: number | string | null;
  meta_proteina_g?: number | string | null;
  meta_carboidrato_g?: number | string | null;
  meta_gordura_g?: number | string | null;
  meta_fibra_g?: number | string | null;
  metas_origem: string;
  metas_fonte?: string | null;
  refeicoes: PlanPayloadMeal[];
}

export function validatePlan(plan: NutritionPlanPayload | null | undefined): string[] {
  const errors: string[] = [];
  if (String(plan?.nome ?? "").trim() === "") errors.push("Informe o nome do plano.");
  const goals = [
    "meta_energia_kcal",
    "meta_proteina_g",
    "meta_carboidrato_g",
    "meta_gordura_g",
    "meta_fibra_g",
  ] as const;
  const record = plan as unknown as Record<string, unknown> | undefined;
  if (
    goals.some((key) => {
      const value = record?.[key];
      return value !== null && value !== undefined && value !== "";
    }) &&
    plan?.metas_origem === "nao_informado"
  ) {
    errors.push("Identifique a origem das metas informadas.");
  }
  if (plan?.metas_origem === "fonte_validada" && String(plan.metas_fonte ?? "").trim() === "") {
    errors.push("Informe a fonte usada para validar as metas.");
  }
  for (const meal of plan?.refeicoes ?? []) {
    if (String(meal.nome).trim() === "") errors.push("Informe o nome de todas as refeições.");
    for (const item of meal.itens) {
      if (
        String(item.alimento).trim() === "" ||
        !(Number(item.quantidade) > 0) ||
        String(item.unidade).trim() === ""
      ) {
        errors.push("Cada item precisa de alimento, quantidade positiva e unidade.");
      }
      if (
        item.valores_origem === "fonte_validada" &&
        hasNutritionValues(item as unknown as Record<string, unknown>) &&
        String(item.valores_fonte ?? "").trim() === ""
      ) {
        errors.push("Informe a fonte dos valores nutricionais validados.");
      }
      for (const replacement of item.substituicoes ?? []) {
        if (
          String(replacement.alimento).trim() === "" ||
          !(Number(replacement.quantidade) > 0) ||
          String(replacement.unidade).trim() === ""
        ) {
          errors.push("Cada substituição precisa de alimento, quantidade positiva e unidade.");
        }
        if (
          replacement.valores_origem === "fonte_validada" &&
          hasNutritionValues(replacement as unknown as Record<string, unknown>) &&
          String(replacement.valores_fonte ?? "").trim() === ""
        ) {
          errors.push("Informe a fonte dos valores nutricionais validados.");
        }
      }
    }
  }
  return [...new Set(errors)];
}
